import { Footer } from "@/components/footer"
import { Target, Users, Lightbulb, Heart } from "lucide-react"
import { SEO } from "@/components/SEO"

export default function AboutPage() {
  return (
    <>
      <SEO
        title="About Us - StayApp | Revolutionizing Multi-Service Booking"
        description="Learn about StayApp's mission to revolutionize service booking across accommodation, safari, dining, nightlife, beauty, and transport with transparent, negotiable pricing."
        keywords="about StayApp, booking platform, travel services, accommodation booking, service marketplace"
        ogImage="/logo.png"
        ogUrl="https://stayapp.com/about"
        canonical="https://stayapp.com/about"
      />
      <main className="pt-28 md:pt-36 lg:pt-40">
      {/* Hero Section */}
      <section className="bg-black text-white py-16 sm:py-20">
        <div className="container mx-auto px-4 sm:px-6 text-center">
          <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold mb-4 sm:mb-6 text-[#75055B] px-2">About StayApp</h1>
          <p className="text-base sm:text-lg md:text-xl text-gray-300 max-w-3xl mx-auto px-4">
            Revolutionizing the way people discover, negotiate, and book services across multiple lifestyle categories
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold text-black mb-6 sm:mb-8 text-center px-2">
              Our Mission
            </h2>
            <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-4 sm:mb-6">
              StayApp was born from a simple idea: booking services shouldn't be complicated or overpriced. We believe
              that everyone deserves access to quality experiences at fair prices, with the ability to negotiate
              directly with service providers.
            </p>
            <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
              Whether you're looking for accommodation, planning a safari adventure, booking a table at a restaurant,
              finding nightlife experiences, scheduling beauty treatments, or hiring a car, StayApp brings all these
              services into one seamless platform with transparent, negotiable pricing.
            </p>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold text-black mb-3 sm:mb-4 px-2">Our Values</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 max-w-6xl mx-auto">
            {[
              {
                icon: Target,
                title: "Transparency",
                description: "Clear pricing, honest reviews, and open communication",
              },
              {
                icon: Users,
                title: "Community",
                description: "Building connections between customers and providers",
              },
              {
                icon: Lightbulb,
                title: "Innovation",
                description: "Constantly improving the booking experience",
              },
              {
                icon: Heart,
                title: "Trust",
                description: "Verified providers and secure transactions",
              },
            ].map((value, index) => (
              <div key={index} className="text-center px-2">
                <div className="w-14 h-14 sm:w-16 sm:h-16 mx-auto rounded-xl bg-[#75055B] flex items-center justify-center mb-3 sm:mb-4">
                  <value.icon className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-black mb-1 sm:mb-2">{value.title}</h3>
                <p className="text-sm sm:text-base text-gray-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold text-black mb-6 sm:mb-8 text-center px-2">
              Our Story
            </h2>
            <div className="space-y-4 sm:space-y-6 text-base sm:text-lg text-gray-700 leading-relaxed">
              <p>
                Founded in 2024, StayApp emerged from the frustration of fragmented booking experiences and inflexible
                pricing. Our founders recognized that travelers and locals alike were juggling multiple apps for
                different services, often paying more than they should.
              </p>
              <p>
                Inspired by the success of negotiation-based platforms, we set out to create a unified solution that
                puts power back in the hands of users. Today, StayApp connects thousands of customers with verified
                service providers across six major categories.
              </p>
              <p>
                From luxury hotels to street food gems, wildlife safaris to city nightlife, premium spas to reliable car
                rentals – we're building a platform that celebrates diverse experiences and fair pricing.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
    </>
  )
}

